/**
 * 091: every sentence the booking form uses to state that a window cannot be priced.
 *
 * `POST /pricing/quote` answers **200** for a refusal — the read succeeded and the platform gave an
 * answer — so this is not a `resultWording.ts` case. That module states a *read's* fate (gone,
 * unauthenticated, refused, failed). This is the sibling of `tenderWording.ts`, which exists for
 * the same reason one step earlier: a refused voucher is likewise a successful read carrying a
 * refusal.
 *
 * Two rules the module exists to hold:
 *
 * - **One sentence per refusal.** An unmet minimum, a rate card that does not cover the dates, and
 *   no usable rate card at all are three different situations. Sharing a message would send the
 *   renter to adjust dates in the one case where adjusting dates cannot possibly help (FR-007).
 * - **No engine identifier reaches the renter** (FR-008). `min_not_met` is a word for a log. The
 *   reported defect was precisely this leaking through, inside a sentence written for the server:
 *   *"Pricing template yields no price for the requested window (min_not_met)"*.
 *
 * The minimum is stated in the unit the administrator configured (FR-009). Minutes render as hours
 * only when the division is exact — a change of notation, not of value. 90 minutes stays 90
 * minutes rather than becoming "1.5 hours", which is a number nobody typed.
 */

/** The refusal outcomes `resolveQuote` can send (`backend/src/modules/pricing/quote.ts:16`). */
export const QUOTE_REFUSAL_OUTCOMES = ['min_not_met', 'not_applicable', 'no_price'] as const;

/** The minimum as the platform states it — see the 091 quote contract. */
export interface QuoteMinimum {
  unit: string;
  value: number;
}

function plural(value: number, noun: string): string {
  return `${value} ${noun}${value === 1 ? '' : 's'}`;
}

/**
 * The minimum in words, or `null` when it cannot be stated **without inventing something**.
 *
 * `null` for an absent minimum (an older backend, which sends none), for a unit this module does
 * not know, and for a non-positive value. In each case the caller falls back to the figure-less
 * sentence: absent means *not stated*, never *no minimum*, and a screen must not print a number the
 * platform did not give it.
 */
function minimumInWords(minimum: QuoteMinimum | undefined): string | null {
  if (!minimum || !Number.isFinite(minimum.value) || minimum.value <= 0) return null;
  if (minimum.unit === 'days') return plural(minimum.value, 'day');
  if (minimum.unit === 'minutes') {
    // Exact hours only. A remainder stays in minutes rather than becoming a decimal.
    return minimum.value % 60 === 0
      ? plural(minimum.value / 60, 'hour')
      : plural(minimum.value, 'minute');
  }
  return null;
}

/**
 * What to tell the renter about a window the platform will not price.
 *
 * `outcome` is the platform's discriminator; `minimum` is present only on `min_not_met`. An outcome
 * this module has never seen still produces a sentence — going blank is the failure mode this
 * feature exists to end, and a screen that says nothing is indistinguishable from one that is
 * broken.
 */
export function refusalSentence(outcome: string, minimum: QuoteMinimum | undefined): string {
  if (outcome === 'min_not_met') {
    const words = minimumInWords(minimum);
    return words
      ? `This car has a minimum rental of ${words}. Choose a longer pick-up and return to see the price.`
      : 'This car cannot be rented for a period this short. Choose a longer pick-up and return to see the price.';
  }
  if (outcome === 'no_price') {
    // Deliberately no mention of dates or a longer period: there is no rate card to satisfy, so
    // every window fails and sending the renter back to the picker wastes their time.
    return 'Pricing is not available for this car right now. Please choose another car.';
  }
  // `not_applicable` — the rate card does not cover these dates — and anything the platform starts
  // sending that this module has not met yet. Both are honestly described by the same sentence:
  // the car cannot be booked for the chosen dates, and a different window may work.
  return 'This car is not available for the dates you chose. Try a different pick-up and return.';
}
